import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { merge, NEVER, Observable, of, switchMap } from 'rxjs';
import { catchError, debounceTime, tap } from 'rxjs/operators';

import {
  AttachmentsActions,
  AttachmentsDataScope,
  getAttachmentStoreScope,
} from '@recruitee/attachments-data';
import { PageVisibilityService } from '@recruitee/common';
import {
  AssignedFlowsActions,
  AssignedJobsActions,
  AvailableFlowsActions,
  RequisitionsActions,
} from '@recruitee/requisitions-data';
import { ProtectedControlsService } from '@recruitee/user-interface';
import { AdminSocket, RequisitionChannel, bufferUntil, fromChannel } from '@recruitee/websocket';

@Injectable()
export class RequisitionChannelService {
  constructor(
    private adminSocket: AdminSocket,
    private store: Store,
    private pageVisibilityService: PageVisibilityService,
    private router: Router,
    private protectedControlsService: ProtectedControlsService,
  ) {}

  public subscribeToWebSocketsChannel(requisitionId: number) {
    return this.adminSocket.subscribeToChannel(`requisition:${requisitionId}`).pipe(
      catchError(err => {
        console.error(err);
        return NEVER;
      }),
      switchMap(channel => this.subscribeToRequisitionsEvents(channel, requisitionId)),
    );
  }

  // eslint-disable-next-line max-lines-per-function
  private subscribeToRequisitionsEvents(channel: RequisitionChannel, requisitionId: number) {
    const pageVisibleTrigger = () =>
      this.pageVisibilityService.isPageVisible()
        ? of(null)
        : this.pageVisibilityService.$onPageVisible;

    return merge(
      this.requisitionUpdatedChannel$(channel, pageVisibleTrigger, requisitionId),
      this.requisitionOffersUpdatedChannel$(channel, pageVisibleTrigger, requisitionId),
      this.requisitionFormUpdatedChannel$(channel, pageVisibleTrigger, requisitionId),
      this.requisitionDeletedChannel$(channel, pageVisibleTrigger, requisitionId),
      this.requisitionApprovalFlowUpdatedChannel$(channel, pageVisibleTrigger, requisitionId),
      this.attachmentsUpdatedChannel$(channel, pageVisibleTrigger, requisitionId),
    );
  }

  private requisitionUpdatedChannel$(
    channel: RequisitionChannel,
    pageVisibleTrigger: () => Observable<any>,
    requisitionId: number,
  ) {
    return fromChannel(channel, 'requisition-updated').pipe(
      bufferUntil(pageVisibleTrigger),
      debounceTime(100),
      tap(() => {
        this.store.dispatch(RequisitionsActions.fetchOne({ payload: { id: requisitionId } }));
        this.store.dispatch(AssignedFlowsActions.fetch({ payload: { requisitionId } }));
      }),
    );
  }

  private requisitionOffersUpdatedChannel$(
    channel: RequisitionChannel,
    pageVisibleTrigger: () => Observable<any>,
    requisitionId: number,
  ) {
    return fromChannel(channel, 'requisition-offers-updated').pipe(
      bufferUntil(pageVisibleTrigger),
      debounceTime(100),
      tap(() => {
        this.store.dispatch(RequisitionsActions.fetchOne({ payload: { id: requisitionId } }));
        this.store.dispatch(AssignedJobsActions.fetchOne({ payload: { id: requisitionId } }));
      }),
    );
  }
  private requisitionFormUpdatedChannel$(
    channel: RequisitionChannel,
    pageVisibleTrigger: () => Observable<any>,
    requisitionId: number,
  ) {
    return fromChannel(channel, 'requisition-form-updated').pipe(
      bufferUntil(pageVisibleTrigger),
      debounceTime(100),
      tap(() => {
        this.store.dispatch(RequisitionsActions.fetchOne({ payload: { id: requisitionId } }));
      }),
    );
  }
  private requisitionDeletedChannel$(
    channel: RequisitionChannel,
    pageVisibleTrigger: () => Observable<any>,
    requisitionId: number,
  ) {
    return fromChannel(channel, 'requisition-deleted').pipe(
      bufferUntil(pageVisibleTrigger),
      debounceTime(100),
      tap(() => {
        this.protectedControlsService.discard();
        this.router.navigate(['/requisitions']);
      }),
    );
  }
  private requisitionApprovalFlowUpdatedChannel$(
    channel: RequisitionChannel,
    pageVisibleTrigger: () => Observable<any>,
    requisitionId: number,
  ) {
    return fromChannel(channel, 'requisition-approval-flow-updated').pipe(
      bufferUntil(pageVisibleTrigger),
      debounceTime(100),
      tap(() => {
        this.store.dispatch(AvailableFlowsActions.fetchOne({ payload: { id: requisitionId } }));
        this.store.dispatch(AssignedFlowsActions.fetch({ payload: { requisitionId } }));
      }),
    );
  }
  private attachmentsUpdatedChannel$(
    channel: RequisitionChannel,
    pageVisibleTrigger: () => Observable<any>,
    requisitionId: number,
  ) {
    return fromChannel(channel, 'attachments-updated').pipe(
      bufferUntil(pageVisibleTrigger),
      debounceTime(100),
      tap(() => {
        const scope = getAttachmentStoreScope(AttachmentsDataScope.Requisition, requisitionId);

        this.store.dispatch(
          AttachmentsActions.fetchAttachmentsByRequisition({
            payload: {
              params: { requisitionId },
              scope,
              replace: true,
            },
          }),
        );
      }),
    );
  }
}
